import { type VariantProps, cva } from 'class-variance-authority';

/**
 * Button variant styles using class-variance-authority
 * Provides consistent button styling across the application
 */
export const buttonVariants = cva(
    // Base styles applied to all buttons
    'inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-600 dark:bg-blue-500 dark:hover:bg-blue-600',
                secondary: 'bg-muted text-foreground hover:bg-muted/80 focus:ring-blue-600',
                outline: 'border border-border bg-background text-foreground hover:bg-muted focus:ring-blue-600',
                ghost: 'text-foreground hover:bg-muted focus:ring-blue-600',
                danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-600 dark:bg-red-500 dark:hover:bg-red-600',
            },
            size: {
                sm: 'h-8 px-3 text-sm',
                md: 'h-10 px-4',
                lg: 'h-12 px-6 text-lg',
            },
            fullWidth: {
                true: 'w-full',
                false: 'w-auto',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
            fullWidth: false,
        },
    }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

/**
 * Card variant styles for consistent card components
 */
export const cardVariants = cva(
    // Base card styles
    'rounded-lg border transition-all duration-200',
    {
        variants: {
            variant: {
                default: 'bg-card border-border shadow-sm',
                outline: 'bg-transparent border-border',
                filled: 'bg-muted border-border',
            },
            padding: {
                none: 'p-0',
                sm: 'p-3',
                md: 'p-4',
                lg: 'p-6',
            },
            interactive: {
                true: 'cursor-pointer hover:shadow-md hover:border-gray-300',
                false: '',
            },
        },
        defaultVariants: {
            variant: 'default',
            padding: 'md',
            interactive: false,
        },
    }
);

export type CardVariantProps = VariantProps<typeof cardVariants>;

/**
 * Input variant styles for form controls
 */
export const inputVariants = cva(
    // Base input styles
    'flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            variant: {
                default: '',
                error: 'border-red-500 focus:border-red-500 focus:ring-red-500',
                success: 'border-green-500 focus:border-green-500 focus:ring-green-500',
            },
            size: {
                sm: 'h-8 px-2 text-xs',
                md: 'h-10 px-3 text-sm',
                lg: 'h-12 px-4 text-base',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'md',
        },
    }
);

export type InputVariantProps = VariantProps<typeof inputVariants>;

/**
 * Badge variant styles for status indicators
 */
export const badgeVariants = cva(
    // Base badge styles
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    {
        variants: {
            variant: {
                default: 'border-transparent bg-gray-900 text-gray-50 dark:bg-gray-100 dark:text-gray-900',
                secondary: 'border-transparent bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100',
                success: 'border-transparent bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200',
                warning: 'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200',
                error: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200',
                outline: 'text-foreground border-border',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

export type BadgeVariantProps = VariantProps<typeof badgeVariants>;

/**
 * Alert variant styles for notifications and messages
 */
export const alertVariants = cva(
    // Base alert styles
    'relative w-full rounded-lg border p-4',
    {
        variants: {
            variant: {
                default: 'bg-background text-foreground border-border',
                info: 'bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-800',
                success: 'bg-green-50 text-green-900 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800',
                warning: 'bg-yellow-50 text-yellow-900 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-200 dark:border-yellow-800',
                error: 'bg-red-50 text-red-900 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

export type AlertVariantProps = VariantProps<typeof alertVariants>;
